import { z } from "zod";

/** Serializable Signature API error shape (safe for callable JSON responses). */
export const signatureApiErrorSchema = z.object({
  type: z.string(),
  title: z.string(),
  status: z.number(),
  detail: z.string().optional(),
  errors: z.array(z.string()).optional(),
});
export type SignatureApiErrorData = z.infer<typeof signatureApiErrorSchema>;

export class SignatureApiError extends Error {
  constructor(
    public type: string,
    public title: string,
    public status: number,
    public detail: string | undefined,
    public errors: string[] | undefined,
  ) {
    super(
      `Signature API error (${status.toString()}) ${title}, ${detail || (errors ? errors.join(", ") : "")}`,
    );
  }

  toJSON(): SignatureApiErrorData {
    return {
      type: this.type,
      title: this.title,
      status: this.status,
      detail: this.detail,
      errors: this.errors,
    };
  }
}

export const EnvelopeMetadataSchema = z.object({
  // keys must be snake_case according to Signature API docs
  project_id: z.string(),
  organization_invitation_id: z.string().optional(),
  invitation_id: z.string().optional(),
});
export const EnvelopeMetadataLooseSchema = z.looseObject({
  ...EnvelopeMetadataSchema.shape,
});
export type EnvelopeMetadata = z.infer<typeof EnvelopeMetadataSchema>;

export const CeremonySchema = z.object({
  url: z.url().nullable(),
  authentication: z.array(
    z.object({
      type: z.enum(["email_link", "email_code"]),
    }),
  ),
});
export const CeremonyLooseSchema = z.looseObject({
  ...CeremonySchema.shape,
});
export type Ceremony = z.infer<typeof CeremonySchema>;

export const RecipientSchema = z.object({
  key: z.string().lowercase().max(32),
  status: z.enum([
    "pending",
    "awaiting",
    "sent",
    "completed",
    "rejected",
    "soft_bounced",
    "hard_bounced",
    "failed",
    "replaced",
  ]),
  ceremony: CeremonySchema.nullable(),
});
export const RecipientLooseSchema = z.looseObject({
  ...RecipientSchema.shape,
  ceremony: CeremonyLooseSchema.nullable(),
});
export type Recipient = z.infer<typeof RecipientSchema>;

export const DeliverableSchema = z.object({
  id: z.string(),
  status: z.enum(["pending", "processing", "generated", "failed"]),
});
export const DeliverableLooseSchema = z.looseObject({
  ...DeliverableSchema.shape,
});
export type Deliverable = z.infer<typeof DeliverableSchema>;

export const EnvelopeSchema = z.object({
  id: z.uuid(),
  status: z.enum(["processing", "in_progress", "completed", "failed", "canceled"]),
  recipients: z.array(RecipientSchema),
  metadata: EnvelopeMetadataSchema,
  deliverable: DeliverableSchema,
});
export const EnvelopeLooseSchema = z.looseObject({
  ...EnvelopeSchema.shape,
  recipients: z.array(RecipientLooseSchema),
  metadata: EnvelopeMetadataLooseSchema,
  deliverable: DeliverableLooseSchema,
});
export type Envelope = z.infer<typeof EnvelopeSchema>;

/**
 * `"client"` is the supervisor org's signer. It keeps its pre-rename name because envelopes already
 * sent to SignatureAPI store it as their recipient key.
 */
export type ContractEnvelopeRecipientKey = "account_holder" | "client";

export const accountHolderHasSigned = (recipients: readonly Pick<Recipient, "key" | "status">[]) =>
  recipients.some(
    (recipient) =>
      recipient.key === ("account_holder" satisfies ContractEnvelopeRecipientKey) &&
      recipient.status === "completed",
  );
